import React from 'react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ArrowRight,
  CheckCircle,
  PaperPlaneTilt,
  Receipt,
  Spinner,
  Trash,
} from '@phosphor-icons/react';

const SignaturesAndTotals = ({
  aideSig,
  setAideSig,
  supSig,
  setSupSig,
  editable,
  tsId,
  totalHours,
  totalUnits,
}) => (
  <div className="flex items-center gap-4 flex-wrap">
    <label className="flex items-center gap-2 cursor-pointer text-xs">
      <Checkbox
        checked={aideSig}
        onCheckedChange={setAideSig}
        disabled={!editable}
        data-testid={`aide-sig-${tsId}`}
      />
      <span className={aideSig ? 'text-emerald-700 font-medium' : 'text-slate-600'}>
        Aide signature {aideSig ? 'completed' : 'pending'}
      </span>
    </label>
    <label className="flex items-center gap-2 cursor-pointer text-xs">
      <Checkbox
        checked={supSig}
        onCheckedChange={setSupSig}
        disabled={!editable}
        data-testid={`sup-sig-${tsId}`}
      />
      <span className={supSig ? 'text-emerald-700 font-medium' : 'text-slate-600'}>
        Supervisor signature {supSig ? 'completed' : 'pending'}
      </span>
    </label>
    <div className="text-xs text-slate-500 border-l border-slate-300 pl-4">
      Total:{' '}
      <span className="font-semibold text-slate-700">{totalHours.toFixed(2)} hrs</span>
      <span className="mx-1.5 text-slate-300">·</span>
      <span className="font-semibold text-indigo-900">{totalUnits.toFixed(0)} units</span>
    </div>
  </div>
);

const PendingReviewActions = ({ tsId, busy, saving, onDelete, onSaveDraft, onConfirm }) => (
  <>
    <Button
      size="sm"
      variant="ghost"
      onClick={() => onDelete(tsId)}
      disabled={busy}
      className="text-red-500 hover:text-red-700 hover:bg-red-50"
      data-testid={`delete-btn-${tsId}`}
    >
      <Trash size={14} className="mr-1" />
      Delete
    </Button>
    <Button
      size="sm"
      variant="outline"
      onClick={onSaveDraft}
      disabled={saving || busy}
      data-testid={`save-draft-${tsId}`}
    >
      {saving ? <Spinner size={14} className="animate-spin" /> : 'Save Draft'}
    </Button>
    <Button
      size="sm"
      onClick={onConfirm}
      disabled={saving || busy}
      className="bg-indigo-900 hover:bg-indigo-800 text-white"
      data-testid={`confirm-btn-${tsId}`}
    >
      {saving ? (
        <Spinner size={14} className="animate-spin" />
      ) : (
        <>
          <CheckCircle size={14} className="mr-1" /> Confirm Timesheet
        </>
      )}
    </Button>
  </>
);

const ConfirmedActions = ({ tsId, busy, onSubmitEvv }) => (
  <>
    <Button
      size="sm"
      onClick={() => onSubmitEvv(tsId)}
      disabled={busy}
      className="bg-indigo-900 hover:bg-indigo-800 text-white"
      data-testid={`evv-btn-${tsId}`}
    >
      <PaperPlaneTilt size={14} className="mr-1" /> Check &amp; Submit EVV
    </Button>
    <Button
      size="sm"
      variant="outline"
      disabled
      title="Create EVV visits first — ODM requires EVV before paying claims"
      className="opacity-60 cursor-not-allowed"
      data-testid={`claims-btn-disabled-${tsId}`}
    >
      <Receipt size={14} className="mr-1" /> Create Claims
    </Button>
  </>
);

const CardFooter = ({
  ts,
  editable,
  aideSig,
  setAideSig,
  supSig,
  setSupSig,
  totalHours,
  totalUnits,
  busy,
  saving,
  onDelete,
  onSaveDraft,
  onConfirm,
  onSubmitEvv,
  onCreateClaims,
  onViewClaims,
}) => (
  <div className="px-5 py-4 bg-slate-50/50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
    <SignaturesAndTotals
      aideSig={aideSig}
      setAideSig={setAideSig}
      supSig={supSig}
      setSupSig={setSupSig}
      editable={editable}
      tsId={ts.id}
      totalHours={totalHours}
      totalUnits={totalUnits}
    />

    <div className="flex items-center gap-2">
      {editable && (
        <PendingReviewActions
          tsId={ts.id}
          busy={busy}
          saving={saving}
          onDelete={onDelete}
          onSaveDraft={onSaveDraft}
          onConfirm={onConfirm}
        />
      )}
      {ts.status === 'confirmed' && (
        <ConfirmedActions tsId={ts.id} busy={busy} onSubmitEvv={onSubmitEvv} />
      )}
      {ts.status === 'submitted_to_evv' && (
        <Button
          size="sm"
          onClick={() => onCreateClaims(ts.id)}
          disabled={busy}
          className="bg-indigo-900 hover:bg-indigo-800 text-white"
          data-testid={`claims-btn-${ts.id}`}
        >
          <Receipt size={14} className="mr-1" /> Create Claims
        </Button>
      )}
      {ts.status === 'claims_created' && (
        <Button
          size="sm"
          variant="outline"
          onClick={onViewClaims}
          data-testid={`view-claims-btn-${ts.id}`}
        >
          <ArrowRight size={14} className="mr-1" /> View Claims
        </Button>
      )}
    </div>
  </div>
);

export default CardFooter;
